/**
 * Bug Explorer cart.
 * Real Shopify cart only: every mutation goes through the locale-aware AJAX Cart API
 * (`/cart/add.js`, `/cart/change.js`) with bundled Section Rendering, so the drawer is
 * re-rendered server-side from the live `cart` object. No client-side cart state.
 */
import { config, postJSON, trapFocus, lockScroll, motionFactor } from '@be/core';

const SECTION = config.cartSection || 'be-cart-drawer';
const routes = config.routes || {};
const addUrl = `${routes.cartAdd || '/cart/add'}.js`;
const changeUrl = `${routes.cartChange || '/cart/change'}.js`;
/** Events this module dispatched itself, so the document listener can ignore them. */
const ownEvents = new WeakSet();
let queue = Promise.resolve();

/** Serialises cart mutations so rapid clicks never race each other. */
function enqueue(task) {
  const run = queue.then(task, task);
  queue = run.catch(() => {});
  return run;
}

function drawer() {
  return /** @type {BeCartDrawer|null} */ (document.querySelector('be-cart-drawer'));
}

/** Applies a Section Rendering API payload to the drawer and every cart count. */
function applySections(sections) {
  const html = sections && sections[SECTION];
  if (!html) return null;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const nextBody = doc.querySelector('[data-be-cart-body]');
  const nextLabel = doc.querySelector('[data-be-cart-label]');
  const d = drawer();
  if (d && nextBody) {
    const body = d.querySelector('[data-be-cart-body]');
    if (body) {
      // Keep keyboard focus on the equivalent control after the line markup is replaced.
      const focused = document.activeElement;
      const restore =
        focused instanceof HTMLElement && body.contains(focused)
          ? { key: focused.closest('[data-key]')?.getAttribute('data-key') || '', cls: focused.className }
          : null;
      body.innerHTML = nextBody.innerHTML;
      body.setAttribute('data-count', nextBody.getAttribute('data-count') || '0');
      body.removeAttribute('aria-busy');
      if (restore) {
        const line = restore.key ? body.querySelector(`[data-key="${CSS.escape(restore.key)}"]`) : null;
        const same = line ? Array.from(line.querySelectorAll('button, a, input')).find((el) => el.className === restore.cls) : null;
        const fallback = same && !(/** @type {HTMLButtonElement} */ (same).disabled) ? same : d.querySelector('[data-be-cart-close]');
        if (fallback instanceof HTMLElement) fallback.focus({ preventScroll: true });
      }
    }
    const label = d.querySelector('[data-be-cart-label]');
    if (label && nextLabel) label.textContent = nextLabel.textContent;
  }
  const count = Number(nextBody?.getAttribute('data-count') || 0);
  updateCounts(count);
  return count;
}

/** @param {number} count */
export function updateCounts(count) {
  document.querySelectorAll('[data-be-cart-count]').forEach((el) => {
    el.textContent = String(count);
    el.toggleAttribute('hidden', count === 0);
  });
  document.querySelectorAll('[data-be-cart-open][data-label-template]').forEach((el) => {
    const template = el.getAttribute('data-label-template') || '';
    el.setAttribute('aria-label', template.replace('[count]', String(count)));
  });
}

/** Dispatches Shopify's standard cart event so apps, pixels and theme listeners stay in sync. */
async function emitStandardEvent(action, lines, settle) {
  try {
    const { CartLinesUpdateEvent } = await import('@shopify/events');
    const deferred = CartLinesUpdateEvent.createPromise();
    const event = new CartLinesUpdateEvent({ action, context: 'product', lines, promise: deferred.promise });
    ownEvents.add(event);
    document.dispatchEvent(event);
    settle
      .then(async () => {
        const cart = await fetch(`${routes.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { source: 'be-cart' } });
      })
      .catch((error) => deferred.reject(error));
  } catch (error) {
    /* Standard events bundle unavailable — the cart itself is unaffected. */
  }
}

/**
 * Adds real variants to the Shopify cart and opens the drawer.
 * @param {{id:number|string, quantity:number, properties?:object, selling_plan?:number}[]} items
 * @param {{ source?: Element|null, open?: boolean }} [options]
 */
export function addToCart(items, options = {}) {
  return enqueue(async () => {
    const request = postJSON(addUrl, {
      items,
      sections: [SECTION],
      sections_url: window.location.pathname,
    });
    emitStandardEvent(
      'add',
      items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      request
    );
    const data = await request;
    const count = applySections(data.sections);
    announce(config.strings.added);
    if (options.open !== false) {
      await fly(options.source);
      drawer()?.open();
    }
    return { data, count };
  });
}

/** @param {string} key @param {number} quantity */
export function changeLine(key, quantity) {
  return enqueue(async () => {
    const body = drawer()?.querySelector('[data-be-cart-body]');
    body?.setAttribute('aria-busy', 'true');
    try {
      const request = postJSON(changeUrl, {
        id: key,
        quantity,
        sections: [SECTION],
        sections_url: window.location.pathname,
      });
      emitStandardEvent(quantity === 0 ? 'remove' : 'update', [{ id: key, quantity }], request);
      const data = await request;
      applySections(data.sections);
      return data;
    } catch (error) {
      body?.removeAttribute('aria-busy');
      announce(error instanceof Error ? error.message : String(error));
      throw error;
    }
  });
}

/** Re-renders the drawer from the server (used after external cart changes and bfcache restores). */
export function refreshCart() {
  return enqueue(async () => {
    const url = `${routes.root || '/'}?sections=${encodeURIComponent(SECTION)}`;
    const data = await fetch(url, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    return applySections(data);
  });
}

function announce(message) {
  const status = drawer()?.querySelector('[data-be-cart-status]');
  if (!status || !message) return;
  status.textContent = '';
  requestAnimationFrame(() => (status.textContent = message));
}

/** Small product token flying from the add button to the header cart. */
function fly(source) {
  const target = document.querySelector('[data-be-cart-target]');
  if (!source || !target || !motionFactor() || !(source instanceof Element)) return Promise.resolve();
  const a = source.getBoundingClientRect();
  const b = target.getBoundingClientRect();
  if (!a.width || !b.width) return Promise.resolve();
  const dot = document.createElement('div');
  dot.className = 'be-fly';
  dot.style.left = `${a.left + a.width / 2 - 14}px`;
  dot.style.top = `${a.top + a.height / 2 - 14}px`;
  document.body.appendChild(dot);
  const dx = b.left + b.width / 2 - (a.left + a.width / 2);
  const dy = b.top + b.height / 2 - (a.top + a.height / 2);
  return new Promise((resolve) => {
    const animation = dot.animate(
      [
        { transform: 'translate(0,0) scale(1)' },
        { transform: `translate(${dx * 0.45}px, ${dy * 0.45 - 120}px) scale(1.15)`, offset: 0.45 },
        { transform: `translate(${dx}px, ${dy}px) scale(.35)`, opacity: 0.7 },
      ],
      { duration: 760, easing: 'cubic-bezier(.55,0,.25,1)' }
    );
    animation.onfinish = () => {
      dot.remove();
      target.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.18)' }, { transform: 'scale(1)' }], {
        duration: 360,
        easing: 'ease-out',
      });
      resolve(undefined);
    };
    animation.oncancel = () => {
      dot.remove();
      resolve(undefined);
    };
  });
}

class BeCartDrawer extends HTMLElement {
  /** @type {(() => void) | null} */
  #release = null;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    if (this.hasAttribute('open')) this.close();
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  open() {
    if (this.isOpen) return;
    this.removeAttribute('inert');
    this.setAttribute('open', '');
    lockScroll(this, true);
    const panel = /** @type {HTMLElement} */ (this.querySelector('.be-cart__panel'));
    this.#release = trapFocus(panel, {
      onEscape: () => this.close(),
      initialFocus: this.querySelector('[data-be-cart-initial]'),
    });
    document.querySelectorAll('[data-be-cart-open]').forEach((el) => el.setAttribute('aria-expanded', 'true'));
  }

  close() {
    if (!this.isOpen) return;
    this.removeAttribute('open');
    this.setAttribute('inert', '');
    lockScroll(this, false);
    this.#release?.();
    this.#release = null;
    document.querySelectorAll('[data-be-cart-open]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const qtyButton = target.closest('[data-be-line-qty]');
    if (qtyButton) {
      const line = qtyButton.closest('[data-key]');
      const key = line?.getAttribute('data-key');
      if (key) changeLine(key, Math.max(0, Number(qtyButton.getAttribute('data-be-line-qty')))).catch(() => {});
      return;
    }

    const upsell = target.closest('[data-be-upsell-add]');
    if (upsell instanceof HTMLButtonElement) {
      upsell.classList.add('is-loading');
      addToCart([{ id: Number(upsell.dataset.beUpsellAdd), quantity: 1 }], { open: false })
        .catch((error) => announce(error.message))
        .finally(() => upsell.classList.remove('is-loading'));
      return;
    }

    const closer = target.closest('[data-be-cart-close]');
    if (closer) {
      // Empty-state links navigate; everything else just closes.
      if (closer.tagName !== 'A') event.preventDefault();
      this.close();
    }
  };
}

if (!customElements.get('be-cart-drawer')) customElements.define('be-cart-drawer', BeCartDrawer);

/* Global triggers: any [data-be-cart-open] element (header cart, CTAs) opens the drawer. */
document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[data-be-cart-open]') : null;
  const d = drawer();
  if (!trigger || !d) return;
  event.preventDefault();
  d.open();
});

/* Cart changes made by other theme components or apps (standard Shopify cart events). */
document.addEventListener('shopify:cart:lines-update', (event) => {
  if (ownEvents.has(event)) return;
  /** @type {any} */ const e = event;
  Promise.resolve(e.promise)
    .then((result) => {
      refreshCart();
      if (e.action === 'add' && !result?.detail?.didError) drawer()?.open();
    })
    .catch(() => refreshCart());
});

/* Back/forward cache restores can show a stale cart. */
window.addEventListener('pageshow', (event) => {
  if (event.persisted) refreshCart();
});

/* Shopify standard action: openCart → this drawer. */
function configureActions() {
  /** @type {any} */ const actions = window.Shopify && /** @type {any} */ (window.Shopify).actions;
  if (!actions?.openCart?.configure) return;
  actions.openCart.configure({
    async handler() {
      const d = drawer();
      if (d) d.open();
      else window.location.href = routes.cart || '/cart';
    },
  });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', configureActions, { once: true });
else configureActions();

export { BeCartDrawer };